/**
 * Responsibility: choose sub-collections for a card that does not exist yet.
 *
 * **Deliberately not the same component as the tag pickers.**
 * `HoldingTagPicker` and `LikedTagPicker` write immediately, because the
 * thing they are tagging already exists and each chip click is a decision
 * that can stand on its own. Here there is nothing to write to: the holding
 * has not been created, or the like has only just been made, so the choice
 * has to be held until the caller has an id to attach it to.
 *
 * So this is controlled and writes nothing. The caller owns the value and
 * performs the write once, after the thing exists.
 *
 * Checkboxes rather than chips because more than one applies at a time and
 * the question being asked is "which of these", not "this one". A row of
 * chips reads as a single choice even when it is not.
 */
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { CollectionTag } from '../../api/client';

type Props = {
  tags: CollectionTag[];
  /** The ids currently ticked. The caller owns this. */
  value: string[];
  onChange: (next: string[]) => void;
  label?: string;
  /** Shown when there is nothing to choose from, so the box is never blank. */
  empty?: string;
};

export function TagCheckList({
  tags,
  value,
  onChange,
  label = 'Also add to sub-collections',
  empty = 'No sub-collections yet. Make one from the collection page.',
}: Props) {
  if (tags.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {empty}
      </Typography>
    );
  }

  const chosen = new Set(value);
  const toggle = (tagId: string) => {
    const next = new Set(chosen);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    onChange([...next]);
  };

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {/* Scrolls rather than growing: somebody with twenty runs on the go
          should not have the Add button pushed off the bottom of a phone. */}
      <FormGroup sx={{ maxHeight: 176, overflowY: 'auto', pl: 0.5 }}>
        {tags.map((tag) => (
          <FormControlLabel
            key={tag.id}
            control={
              <Checkbox
                size="small"
                checked={chosen.has(tag.id)}
                onChange={() => toggle(tag.id)}
              />
            }
            label={
              <Typography variant="body2" noWrap>
                {tag.name}
                {tag.goal !== null ? (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {` — ${tag.held}/${tag.goal}`}
                  </Typography>
                ) : null}
              </Typography>
            }
            sx={{ mr: 0, minWidth: 0 }}
          />
        ))}
      </FormGroup>
    </Stack>
  );
}
